import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import type { NotifyApi } from '../../components/notifications';
import { ClientDetailModal } from './ClientDetailModal';
import { AiCombo } from '../../components/AiCombo';
import { CheckedCombo } from '../../components/CheckedCombo';
import { serverMessage } from '../../components/confirm/serverMessage';
import { useModalKeys } from '../../hooks/useModalKeys';
import { MaintenanceDetailFrame, type MaintenanceDetailWiring } from '../maintenance/MaintenanceDetailFrame';
import { MAINT_BASE } from '../maintenance/maintenance';
import { SrBody } from '../maintenance/srLayout';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "Project" dialog — opened by New and by the row Open command on the
// Client detail's Projects tab. Replaces the earlier 3-field mini form.
//
// Field set + layout ported from the legacy BO (Setting/Project.cs), left column
// then right column exactly as GoLive lays them out:
//   LEFT   Project ID* · Project Name · Client (read-only) · Project Location ·
//          Project Category · Project Code · To · CC
//   RIGHT  Project City · Project Overview · Project Source · Sample Retention ·
//          Comment · Retired By · Retired Date
//
// Legacy column names are preserved (§5.2): "Sample Retention" is
// DaysSampleKeeping, and To / CC are ReportingMailTo / ReportingMailCCTo — the
// SAME two columns the sample-receipt send path reads when resolving recipients,
// which is why they are the client's contact addresses rather than free text.
// ─────────────────────────────────────────────────────────────────────────────

interface Opt { id: string; name: string }
interface ClientRow { oid: string; name: string; address: string; phone: string }

interface ProjectForm {
  projectId: string; projectName: string; projectLocation: string;
  projectCategoryId: string; projectCode: string;
  reportingMailTo: string; reportingMailCCTo: string;
  projectCity: string; projectOverview: string; projectSource: string;
  daysSampleKeeping: string; comment: string;
  retiredById: string; retiredDate: string;
}
const EMPTY: ProjectForm = {
  projectId: '', projectName: '', projectLocation: '',
  projectCategoryId: '', projectCode: '', reportingMailTo: '', reportingMailCCTo: '',
  projectCity: '', projectOverview: '', projectSource: '',
  daysSampleKeeping: '', comment: '', retiredById: '', retiredDate: '',
};
type TextKey = 'projectId' | 'projectName' | 'projectCode' | 'projectCity'
  | 'projectOverview' | 'projectSource' | 'daysSampleKeeping';

// ReportingMailTo / ReportingMailCCTo store a ';'-joined list of CONTACT OIDS
// (legacy XAF CheckedListBoxEditor keys, NOT addresses): the sample-receipt send
// path parses them as GUIDs and resolves each to a report contact's Email/EmailCC.
// Parse to a lowercased id array for the multi-select, and re-join with "; ".
const splitIds = (raw: string): string[] =>
  raw ? raw.split(/[;,\r\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean) : [];
const joinIds = (vals: string[]): string => vals.join('; ');

// G5 orders the To/CC dropdowns by the contact's primary Email, then by Oid
// (Project.cs GetCheckedListBoxItems :74/:85 — ThenBy(i => i.Oid)). .NET
// Guid.CompareTo compares Data1 as an UNSIGNED int32, Data2/Data3 as UNSIGNED
// int16, then the final 8 bytes — NOT plain string order — so keep the raw
// unsigned values (verified against the live City-of-Houston contact oids,
// 2026-11-04: 10<53<57<7A<D7<EB<FA, which a signed compare got wrong).
const guidFields = (s: string): { d1: number; d2: number; d3: number; bytes: number[] } | null => {
  const m = /^([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{12})$/i.exec((s ?? '').trim());
  if (!m) return null;
  const tail = (m[4] ?? '') + (m[5] ?? '');       // 16 hex chars = the 8 trailing bytes
  const bytes = Array.from({ length: 8 }, (_, i) => parseInt(tail.slice(i * 2, i * 2 + 2), 16));
  return { d1: parseInt(m[1] ?? '', 16), d2: parseInt(m[2] ?? '', 16), d3: parseInt(m[3] ?? '', 16), bytes };
};
const compareGuid = (a: string, b: string): number => {
  const fa = guidFields(a), fb = guidFields(b);
  if (!fa || !fb) return a < b ? -1 : a > b ? 1 : 0;   // fall back to string order
  if (fa.d1 !== fb.d1) return fa.d1 < fb.d1 ? -1 : 1;
  if (fa.d2 !== fb.d2) return fa.d2 < fb.d2 ? -1 : 1;
  if (fa.d3 !== fb.d3) return fa.d3 < fb.d3 ? -1 : 1;
  for (let i = 0; i < 8; i++) {
    const x = fa.bytes[i] ?? 0, y = fb.bytes[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
};

/** Page-mode wiring supplied by the Maintenance list (breadcrumb, prev / next, New · Delete · Refresh · Audit). */
export type ProjectDetailPage = MaintenanceDetailWiring;

export function ProjectDetailModal({ clientId, clientName, projectOid, onClose, onSaved, notify, page }: {
  clientId: string;
  clientName: string;
  projectOid: string | null;        // null = New
  onClose: () => void;
  /** `ownerClientId` is the client the project was saved under — a project opened with no client (Maintenance ▸ Project ▸
   *  New) picks it in the form, so the caller needs it to reopen the saved record. */
  onSaved: (savedOid?: string, ownerClientId?: string) => void;
  notify: NotifyApi;
  /** When set the editor renders as the full-page Detail View (Maintenance ▸ Project) instead of a popup. */
  page?: ProjectDetailPage;
}) {
  const [f, setF] = useState<ProjectForm>(EMPTY);
  const retiredFull = useRef('');
  const [loading, setLoading] = useState(!!projectOid);
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(false);
  const [categories, setCategories] = useState<Opt[]>([]);
  // To = report contacts' Email; CC = report contacts' EmailCC (GoLive
  // Project.GetCheckedListBoxItems — CC binds EmailCC, not Email).
  const [toOpts, setToOpts] = useState<Opt[]>([]);
  const [ccOpts, setCcOpts] = useState<Opt[]>([]);
  // GoLive Client field is a 3-column grid picker (Client/Address/Phone).
  const [clientRows, setClientRows] = useState<ClientRow[]>([{ oid: clientId, name: clientName || 'N/A', address: '', phone: '' }]);
  // GoLive opens the Project dialog with Client = N/A (nothing pre-selected).
  // (A full-page edit shows the project's own client instead — the page has room, and N/A there reads as 'no client'.)
  const [selectedClientId, setSelectedClientId] = useState<string>(page ? clientId : '');
  const [projCatOpen, setProjCatOpen] = useState<false | 'new' | 'edit'>(false);
  // The Client lookup's ＋ opens a NEW client (and selects it once saved); the pencil edits the selected one.
  const [clientEditOpen, setClientEditOpen] = useState<false | 'new' | 'edit'>(false);
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });

  // Lookups: Client (every client), Project Category (dbo.ProjectCategory),
  // Retired By (Employee), To/CC (this client's contact e-mail addresses).
  const loadCategories = useCallback(() => api.get('/api/v1/clients/lookups/project-categories')
    .then(r => setCategories((r.data as { data?: Opt[] }).data ?? [])).catch(() => {}), []);
  useEffect(() => {
    void loadCategories();
    // Maintenance ▸ Project offers G5's own Client lookup (active clients only: not closed, not retired); the popups keep the full list.
    const loadClients: Promise<ClientRow[]> = page
      ? api.get(`${MAINT_BASE}/clients?scope=active`).then(r => ((r.data as { data?: { oid: string; client: string | null; address1: string | null; phone: string | null }[] }).data ?? [])
          .map(c => ({ oid: c.oid, name: c.client ?? '', address: c.address1 ?? '', phone: c.phone ?? '' })))
      : api.get('/api/v1/sample-receipt-notification/clients').then(r => (r.data as { data?: ClientRow[] }).data ?? []);
    void loadClients
      .then(rows => {
        if (!rows.length) return;
        // The project's own client must always be pickable/shown, even when that list omits it. A NEW project opened
        // straight from Maintenance ▸ Project has no client yet (clientId ''), so there is nothing to add.
        const has = !clientId || rows.some(c => c.oid.toLowerCase() === clientId.toLowerCase());
        setClientRows(has ? rows : [{ oid: clientId, name: clientName || 'N/A', address: '', phone: '' }, ...rows]);
      }).catch(() => {});
  }, [clientId, clientName, loadCategories, page]);


  // To / CC option lists = the (effective) client's REPORT contacts (type 0 =
  // IsReport), refreshed when the Client is switched — G5's GetCheckedListBoxItems
  // reflects the CURRENT client. Option VALUE = Contact Oid (what the column
  // stores); LABEL (Opt.name) = Email (To) / EmailCC (CC). Empty addresses skipped.
  useEffect(() => {
    const cid = selectedClientId || clientId;
    if (!cid) { setToOpts([]); setCcOpts([]); return; }
    let cancelled = false;
    void api.get(`/api/v1/clients/${encodeURIComponent(cid)}/contacts?type=0`)
      .then(r => {
        if (cancelled) return;
        type C = { oid: string; email: string | null; emailCc: string | null };
        const cs = (r.data as { data?: C[] }).data ?? [];
        // G5 orders BOTH To and CC by the contact's primary Email ascending, then by
        // Oid (Project.cs :74/:85) — so CC is ordered by Email even though it DISPLAYS
        // EmailCC, and empty-Email contacts bubble to the top in Oid order.
        const ordered = [...cs].sort((a, b) => {
          const ea = (a.email ?? '').trim().toLowerCase();
          const eb = (b.email ?? '').trim().toLowerCase();
          if (ea !== eb) return ea < eb ? -1 : 1;
          return compareGuid((a.oid ?? '').toLowerCase(), (b.oid ?? '').toLowerCase());
        });
        const rows = (pick: (c: C) => string | null): Opt[] =>
          ordered.map(c => ({ id: (c.oid ?? '').toLowerCase(), name: (pick(c) ?? '').trim() }))
                 .filter(o => o.id && o.name);
        setToOpts(rows(c => c.email));
        setCcOpts(rows(c => c.emailCc));
      }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedClientId, clientId]);

  // Seed an edit from the FULL record (the grid row is only four columns).
  useEffect(() => {
    if (!projectOid) { setF({ ...EMPTY }); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    void api.get(`/api/v1/clients/${encodeURIComponent(clientId || "00000000-0000-0000-0000-000000000000")}/projects/${projectOid}`)
      .then(r => {
        if (cancelled) return;
        const d = (r.data as { data?: Record<string, unknown> }).data ?? {};
        const s = (k: string) => String(d[k] ?? '');
        retiredFull.current = s('retiredDate');
        setF({
          projectId: s('projectId'), projectName: s('projectName'), projectLocation: s('projectLocation'),
          projectCategoryId: s('projectCategoryId'), projectCode: s('projectCode'),
          reportingMailTo: s('reportingMailTo'), reportingMailCCTo: s('reportingMailCCTo'),
          projectCity: s('projectCity'), projectOverview: s('projectOverview'),
          projectSource: s('projectSource'), daysSampleKeeping: s('daysSampleKeeping'),
          comment: s('comment'), retiredById: s('retiredById'), retiredDate: s('retiredDate').slice(0, 10),
        });
      })
      .catch(() => { if (!cancelled) notify.error('Failed to load the project.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectOid, clientId, notify]);

  // The write itself — throws on failure. Shared by the popup's OK and the page's Save set, so a project is stored one way.
  const persist = useCallback(async (): Promise<string | undefined> => {
    // The date box shows the day only; an unchanged day sends the stored value back whole so its time of day survives the save.
    const keepRetired = !!f.retiredDate && retiredFull.current.slice(0, 10) === f.retiredDate;
    const body = { ...f, retiredDate: keepRetired ? retiredFull.current : (f.retiredDate || null), projectCategoryId: f.projectCategoryId || null,
                   retiredById: f.retiredById || null };
    // The project attaches to the SELECTED client (GoLive Client lookup); fall
    // back to the client being edited if the picker was cleared.
    const owner = selectedClientId || clientId;
    if (projectOid) { await api.put(`/api/v1/clients/${encodeURIComponent(owner)}/projects/${projectOid}`, body); return projectOid; }
    const res = await api.post(`/api/v1/clients/${encodeURIComponent(owner)}/projects`, body);
    return (res.data as { data?: { oid?: string } }).data?.oid;
  }, [f, projectOid, clientId, selectedClientId]);

  const save = useCallback(async () => {
    if (!f.projectId.trim()) { notify.warning('Project ID is required.'); return; }
    if (!(selectedClientId || clientId)) { notify.warning("'Client' must not be empty."); return; }
    setSaving(true);
    try {
      await persist();
      notify.success('Saved successfully.');
      onSaved();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save the project.'); }
    finally { setSaving(false); }
  }, [f, notify, onSaved, persist, selectedClientId, clientId]);

  const input =(highlight: boolean): CSSProperties => ({
    width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5,
    boxSizing: 'border-box', fontSize: 12, background: highlight ? '#fffbe7' : '#fff',
  });
  // Sample Registration layout on the Maintenance page: <label> + control are direct children of the .sr-fgrid column.
  const row = (label: string, control: ReactNode, req = false, top = false) => page ? (
    <><label className={req ? 'req' : undefined} style={top ? { alignSelf: 'start', paddingTop: 3 } : undefined}>{label}</label>{control}</>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: '116px 1fr', alignItems: top ? 'start' : 'center', gap: 8, marginBottom: 7 }}>
      <label style={{ fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap', color: '#4a4f58' }}>{label}:{req && <span style={{ color: '#d33' }}>*</span>}</label>
      {control}
    </div>
  );
  const text = (key: TextKey, req = false): ReactNode => (
    <input type="text" value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))}
           style={input(req && !f[key].trim())} />
  );
  // GoLive lookup: compact scrollable combobox · edit (New/Edit) · eraser
  // (clear). Uses the shared <Combo> (capped-height portal list) — matches the
  // ClientDetail lookups (e.g. the Retired By / Owner combobox), not a native
  // full-height <select>.
  const lookupCombo = (value: string, opts: Opt[], onChange: (v: string) => void, onEdit?: () => void): ReactNode => (
    <AiCombo value={value || null} options={opts.map(o => ({ value: o.id, label: o.name }))}
             onChange={v => onChange(v ?? '')} onNew={onEdit} newTitle="New / Edit" erasable />
  );
  // To / CC are MULTI-recipient (legacy XAF CheckedListBoxEditor): the column holds
  // a ';'-joined list of CONTACT OIDS, so the shared multi-select CheckedCombo runs
  // over the oids and maps each to its e-mail for display (labelOf) — the value
  // stays the oid, exactly what the send path resolves. Only oids that map to a
  // current report contact are SHOWN (GoLive hides the rest — a stored oid for a
  // non-report / removed contact resolves to no address and is dropped from the
  // display). Those unresolved oids are NOT shown but ARE kept in the stored string
  // on save, so a recipient is never silently dropped. The trigger lists each
  // resolved pick's address joined by '; ' like GoLive.
  const mailCombo = (value: string, opts: Opt[], onChange: (v: string) => void): ReactNode => {
    const stored = splitIds(value);
    const labelMap = new Map(opts.map(o => [o.id, o.name]));
    const options = opts.map(o => o.id);                          // resolvable report-contact oids
    const shown = stored.filter(id => labelMap.has(id));          // show only what resolves (GoLive parity)
    const passthrough = stored.filter(id => !labelMap.has(id));   // unresolved oids: kept, not shown
    return (
      <CheckedCombo options={options} values={shown}
                    onChange={vals => onChange(joinIds([...vals, ...passthrough]))}
                    labelOf={(id) => labelMap.get(id) ?? id}
                    placeholder="N/A" summaryJoin="; " unit="recipients" caretAccent />
    );
  };
  const area = (value: string, onChange: (v: string) => void, maxLength: number, rows = 3): ReactNode => (
    <textarea rows={rows} value={value} maxLength={maxLength} onChange={e => onChange(e.target.value)}
              style={{ ...input(false), resize: 'vertical' }} />
  );

  const dateStyle = <style>{'.pdm-date.pdm-date-empty:not(:focus)::-webkit-datetime-edit{color:transparent}'}</style>;

  const bodyEl = loading ? (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
    ) : (
      <SrBody page={!!page} title="Project Information" icon="📁" popupStyle={{ padding: 16, overflowY: 'auto' }}>
        <div className={page ? 'maint-cols' : undefined} style={page ? undefined : { display: 'flex', gap: 22 }}>
          <div className={page ? 'sr-fgrid maint-fg2' : undefined} style={page ? undefined : { flex: 1 }}>
            {row('Project ID', text('projectId', true), true)}
            {row('Project Name', text('projectName'))}
            {/* Client is GoLive's 3-column grid picker (Client/Address/Phone +
                per-column filter + pager), defaulting to N/A. The ✎ opens the
                full Client detail. Changing it targets the POST/PUT. */}
            {row('Client', <ClientGridPicker value={selectedClientId} rows={clientRows}
                   onChange={setSelectedClientId}
                   onNew={() => setClientEditOpen('new')}
                   onEdit={selectedClientId || clientId ? () => setClientEditOpen('edit') : undefined} />, !clientId || !!page)}
            {row('Project Location', area(f.projectLocation, v => setF(p => ({ ...p, projectLocation: v })), 300), false, true)}
            {row('Project Category', (
              <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {lookupCombo(f.projectCategoryId, categories, v => setF(p => ({ ...p, projectCategoryId: v })), () => setProjCatOpen('new'))}
                </div>
                {f.projectCategoryId && (
                  <button type="button" className="ca-btn" title="Edit the selected category" aria-label="Edit project category"
                          onClick={() => setProjCatOpen('edit')}><i className="ti ti-pencil" /></button>
                )}
              </div>
            ))}
            {row('Project Code', text('projectCode'))}
            {/* To / CC feed dbo.Project.ReportingMailTo / ReportingMailCCTo — the
                ';'-joined recipient lists the sample-receipt send path resolves
                against. MULTI-select (legacy CheckedListBoxEditor): To binds the
                report contacts' Email; CC binds their EmailCC. */}
            {row('To', mailCombo(f.reportingMailTo, toOpts, v => setF(p => ({ ...p, reportingMailTo: v }))))}
            {row('CC', mailCombo(f.reportingMailCCTo, ccOpts, v => setF(p => ({ ...p, reportingMailCCTo: v }))))}
          </div>
          <div className={page ? 'sr-fgrid maint-fg2' : undefined} style={page ? undefined : { flex: 1 }}>
            {row('Project City', text('projectCity'))}
            {row('Project Overview', text('projectOverview'))}
            {row('Project Source', text('projectSource'))}
            {row('Sample Retention', text('daysSampleKeeping'))}
            {row('Comment', area(f.comment, v => setF(p => ({ ...p, comment: v })), 1000), false, true)}
            {row('Retired Date', <input type="date" className={f.retiredDate ? 'pdm-date' : 'pdm-date pdm-date-empty'}
                                        value={f.retiredDate} onChange={e => setF(p => ({ ...p, retiredDate: e.target.value }))}
                                        style={input(false)} />)}
          </div>
        </div>
      </SrBody>
    );

  const nestedEl = (
    <>
      {/* Project Category ✎ → GoLive "Project Category" popup (dbo.ProjectCategory). */}
      {projCatOpen && (
        <ProjectCategoryModal
          categoryId={projCatOpen === 'edit' ? f.projectCategoryId : null}
          onClose={() => setProjCatOpen(false)}
          onSaved={async (savedId) => { setProjCatOpen(false); await loadCategories(); if (savedId) setF(p => ({ ...p, projectCategoryId: savedId })); }}
          notify={notify} />
      )}

      {/* Client ✎ → the FULL Client detail popup (GoLive parity). Nested above
          this Project popup (zIndex 1700 > 1500). */}
      {clientEditOpen && (
        <ClientDetailModal clientId={clientEditOpen === 'edit' ? (selectedClientId || clientId) : null} zIndex={1700}
          onClose={() => setClientEditOpen(false)}
          onSaved={(id) => {
            // A client created from here becomes the project's client (and is offered in the lookup from now on).
            if (clientEditOpen === 'new' && id) {
              void api.get(`/api/v1/clients/${encodeURIComponent(id)}`).then(r => {
                const d = (r.data as { data?: { customerName?: string; address?: string; officePhone?: string } }).data ?? {};
                setClientRows(rows => rows.some(x => x.oid.toLowerCase() === id.toLowerCase()) ? rows
                  : [{ oid: id, name: d.customerName ?? '', address: d.address ?? '', phone: d.officePhone ?? '' }, ...rows]);
                setSelectedClientId(id);
              }).catch(() => { /* the client exists; it just is not pre-selected */ });
            }
            setClientEditOpen(false);
          }}
          notify={notify} />
      )}
    </>
  );

  // Full-page Detail View (standing rule 12): the Maintenance list is REPLACED by this page.
  if (page) {
    return (
      <MaintenanceDetailFrame
        entityLabel="Project" recordName={f.projectName.trim() || f.projectId.trim() || (projectOid ? '' : 'New')} listTitle={page.listTitle}
        loading={loading} formName="Client Master Data" isNew={!projectOid}
        validate={() => (!f.projectId.trim() ? 'Project ID is required.' : !(selectedClientId || clientId) ? "'Client' must not be empty." : null)}
        persist={persist} onAfterSave={(id) => onSaved(id, selectedClientId || clientId)} onNew={page.onNew} actions={page}
        onHome={page.onHome} onClose={onClose}
        prevId={page.prevId} nextId={page.nextId} onNavigate={page.onNavigate}
        suspendKeys={!!projCatOpen || !!clientEditOpen}
      >
        {dateStyle}
        {bodyEl}
        {nestedEl}
      </MaintenanceDetailFrame>
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
           style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 820, maxWidth: '96vw',
                    height: max ? '94vh' : undefined, maxHeight: '94vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        {dateStyle}
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                      color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{f.projectName.trim() ? `${f.projectName} - Project` : 'Project'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>

        {bodyEl}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving || loading} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {nestedEl}
    </div>
  );
}

// ── Project Category ✎ popup — GoLive "Project Category" (Category Name* /
//    Description) → dbo.ProjectCategory. ──────────────────────────────────────
function ProjectCategoryModal({ categoryId, onClose, onSaved, notify }: {
  /** null = New; an id = Edit that category. */
  categoryId: string | null;
  onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nonCommercial, setNonCommercial] = useState(0);   // 0 = None, 1 = Yes
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(false);
  useEffect(() => {
    if (!categoryId) return;
    void api.get(`/api/v1/clients/lookups/project-categories/${encodeURIComponent(categoryId)}`)
      .then(r => {
        const d = (r.data as { data?: { category?: string; description?: string; nonCommercial?: number } }).data ?? {};
        setName(d.category ?? ''); setDescription(d.description ?? ''); setNonCommercial(d.nonCommercial === 1 ? 1 : 0);
      }).catch(() => notify.error('Failed to load the project category.'));
  }, [categoryId, notify]);
  const inp: CSSProperties = { width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5, boxSizing: 'border-box', fontSize: 12 };
  const save = useCallback(async () => {
    if (!name.trim()) { notify.warning("'Category Name' must not be empty."); return; }
    setSaving(true);
    try {
      if (categoryId) {
        await api.put(`/api/v1/clients/lookups/project-categories/${encodeURIComponent(categoryId)}`, { category: name, description, nonCommercial });
        notify.success('Saved successfully.');
        onSaved(categoryId);
      } else {
        const res = await api.post('/api/v1/clients/lookups/project-categories', { category: name, description, nonCommercial });
        notify.success('Saved successfully.');
        onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
      }
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save the project category.'); }
    finally { setSaving(false); }
  }, [name, description, nonCommercial, notify, onSaved, categoryId]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 460, maxWidth: '96vw', height: max ? '94vh' : undefined, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Project Category</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>
        <div style={{ padding: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: '#4a4f58' }}>Category Name:<span style={{ color: '#d33' }}>*</span></label>
            <input style={{ ...inp, background: name.trim() ? '#fff' : '#fffbe7' }} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'start', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 11 }}>Description:</label>
            <textarea rows={3} style={{ ...inp, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          {/* Non Commercial — legacy ProjectCategory.CommercialType None(0)/Yes(1). */}
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 11 }}>Non Commercial:</label>
            <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="nc" checked={nonCommercial === 0} onChange={() => setNonCommercial(0)} /> None
              </label>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="nc" checked={nonCommercial === 1} onChange={() => setNonCommercial(1)} /> Yes
              </label>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Client lookup in the Project dialog = GoLive's 3-column grid picker
//    (Client / Address / Phone) with a per-column filter row + pager, mirroring
//    the Sample Receipt Client picker. Same buttons as every other lookup: ＋ (New / Edit → full Client detail), caret, eraser.
function ClientGridPicker({ value, rows, onChange, onNew, onEdit }: {
  value: string; rows: ClientRow[]; onChange: (oid: string) => void; onNew?: () => void; onEdit?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [fName, setFName] = useState(''); const [fAddr, setFAddr] = useState(''); const [fPhone, setFPhone] = useState('');
  const [page, setPage] = useState(0); const pageSize = 20;
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const selected = rows.find(r => r.oid.toLowerCase() === value.toLowerCase());

  const place = () => { const r = btnRef.current?.getBoundingClientRect(); if (!r) return; setPos({ top: Math.min(r.bottom + 2, window.innerHeight - 340), left: r.left, width: Math.max(r.width, 560) }); };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return; setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const filtered = rows.filter(r =>
    (!fName || r.name.toLowerCase().includes(fName.toLowerCase())) &&
    (!fAddr || (r.address || '').toLowerCase().includes(fAddr.toLowerCase())) &&
    (!fPhone || (r.phone || '').toLowerCase().includes(fPhone.toLowerCase())));
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(cur * pageSize, (cur + 1) * pageSize);

  const th: CSSProperties = { textAlign: 'left', padding: '5px 8px', borderBottom: '1px solid #e5e7eb', color: '#4a4f58', fontSize: 11.5 };
  const fi: CSSProperties = { width: '100%', padding: '2px 5px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };
  const td: CSSProperties = { padding: '5px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 };
  const pg = (disabled: boolean): CSSProperties => ({ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#c3c9d2' : '#4a4f58' });

  return (
    <div className="ai-lkbox aic" onKeyDown={(e) => {
      // Esc closes only this picker — stop it bubbling (React portal events bubble
      // to ancestors) to the modal's shared Esc handler (standing rule 14).
      if (open && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    }}>
      <button type="button" className="aic-trigger" ref={btnRef} onClick={() => setOpen(o => !o)}>
        <span className="aic-val">{selected?.name || 'N/A'}</span>
      </button>
      {onNew && <button type="button" className="aic-new" title="New" onClick={onNew}>＋</button>}
      {onEdit && <button type="button" className="aic-erase" title="Edit" aria-label="Edit" onClick={onEdit}><i className="ti ti-pencil" aria-hidden="true" /></button>}
      <button type="button" className="aic-caret" title="Show list" onClick={() => setOpen(o => !o)}><span className="aic-caret-tri" aria-hidden="true" /></button>
      <button type="button" className="aic-erase" tabIndex={-1} title="Clear" aria-label="Clear" onClick={(e) => { e.stopPropagation(); onChange(''); }}><i className="ti ti-eraser" aria-hidden="true" /></button>
      {open && pos && createPortal(
        <div ref={popRef} className="pdm-clientpick" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 320, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', zIndex: 2100, display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafbfc' }}><th style={th}>Client</th><th style={th}>Address</th><th style={th}>Phone</th></tr>
                <tr style={{ background: '#fafbfc' }}>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fName} onChange={e => { setFName(e.target.value); setPage(0); }} /></th>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fAddr} onChange={e => { setFAddr(e.target.value); setPage(0); }} /></th>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fPhone} onChange={e => { setFPhone(e.target.value); setPage(0); }} /></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>No data to display</td></tr>
                ) : pageRows.map(r => (
                  <tr key={r.oid} onClick={() => { onChange(r.oid); setOpen(false); }}
                      data-sel={r.oid.toLowerCase() === value.toLowerCase() ? '1' : undefined}
                      style={{ cursor: 'pointer', background: r.oid.toLowerCase() === value.toLowerCase() ? '#fff3ea' : '#fff' }}>
                    <td style={td}>{r.name}</td><td style={td}>{r.address}</td><td style={td}>{r.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: 11.5, color: '#4a4f58', background: '#fafbfc' }}>
            <span>Page {cur + 1} of {totalPages} ({filtered.length} item{filtered.length === 1 ? '' : 's'})</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button type="button" disabled={cur <= 0} onClick={() => setPage(p => Math.max(0, p - 1))} style={pg(cur <= 0)}><i className="ti ti-chevron-left" /></button>
              <button type="button" disabled={cur >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} style={pg(cur >= totalPages - 1)}><i className="ti ti-chevron-right" /></button>
            </span>
          </div>
        </div>, document.body)}
    </div>
  );
}
